import { assign, createMachine, Sender } from "xstate";
import { IOfferMode } from "@nju/data-access-portal-offers";
import {
  createOrchestrator,
  fetchOrchestrators,
  getOrchestratorForMode,
  IFSecureOrchestrator,
  IFtthOrchestrator,
  IMobileOrchestrator,
  IMultiOrderOrchestrator,
  onboardingInProgress,
} from "@nju/data-access-portal-orchestrator";
import { createFtthOrder } from "@nju/data-access-portal-order";
import {
  IInfoResponse,
  info,
  login,
  loginWithIdentityProvider,
} from "@nju/data-access-portal-user";
import { pushloginEvent } from "@nju/data-layer";
import { portalRoutes } from "@nju/routes";
import { isValidEmail } from "@nju/utils";

interface IContext {
  authCode?: string;
  email: string;
  password: string;
  emailError?: string;
  passwordError?: string;
  formError?: string;
  additionalFormError?: string;
  flow?: "ONBOARDING" | "multiOrder";
  offerIds?: string[];
  mode: IOfferMode;
}

type IEvent =
  | {
      type: "SET_INPUT_VALUE";
      name: "email" | "password";
      value: string;
    }
  | {
      type: "LOGIN_SUCCESSFUL";
      orchestrator?:
        | IFtthOrchestrator
        | IMobileOrchestrator
        | IMultiOrderOrchestrator
        | IFSecureOrchestrator;
      user?: IInfoResponse;
    }
  | {
      type: "INVALID_ORCHESTRATOR_STATE";
      orchestrator:
        | IFtthOrchestrator
        | IMobileOrchestrator
        | IMultiOrderOrchestrator;
    }
  | {
      type: "ANOTHER_ORDER_IN_PROGRESS";
    }
  | { type: "GENERAL_ERROR" }
  | { type: "SUBMIT" };

export const loginMachine = createMachine(
  {
    context: {
      email: "",
      password: "",
      mode: "ftth",
    },
    schema: {
      context: {} as IContext,
      events: {} as IEvent,
    },
    initial: "init",
    states: {
      init: {
        always: [
          {
            cond: "hasAuthCode",
            target: "loginWithCode",
          },
          {
            target: "ready",
          },
        ],
      },
      ready: {
        on: {
          SET_INPUT_VALUE: {
            actions: "setInputValue",
          },
          SUBMIT: {
            target: "validate",
          },
        },
      },
      validate: {
        entry: "validateForm",
        always: [
          {
            cond: "hasNoInputErrors",
            target: "loginWithForm",
          },
          {
            target: "ready",
          },
        ],
      },
      loginWithForm: {
        tags: ["loading"],
        invoke: {
          src: "loginWithForm",
        },
        on: {
          GENERAL_ERROR: {
            actions: "setFormError",
            target: "ready",
          },
          LOGIN_SUCCESSFUL: {
            actions: "onSubmit",
          },
          INVALID_ORCHESTRATOR_STATE: {
            actions: "onInvalidOrchestratorState",
          },
          ANOTHER_ORDER_IN_PROGRESS: {
            actions: "onAnotherOrderInProgress",
          },
        },
      },
      loginWithCode: {
        tags: ["loading"],
        exit: "forgetAuthCode",
        invoke: {
          src: "loginWithCode",
        },
        on: {
          GENERAL_ERROR: {
            actions: "setFormError",
            target: "ready",
          },
          LOGIN_SUCCESSFUL: {
            actions: "onSubmit",
          },
          INVALID_ORCHESTRATOR_STATE: {
            actions: "onInvalidOrchestratorState",
          },
          ANOTHER_ORDER_IN_PROGRESS: {
            actions: "onAnotherOrderInProgress",
          },
        },
      },
    },
  },
  {
    actions: {
      forgetAuthCode: assign<IContext, IEvent>(() => {
        return {
          authCode: undefined,
        };
      }),
      setFormError: assign<IContext, IEvent>((context, event) => {
        if (event.type === "GENERAL_ERROR") {
          return {
            formError:
              "podane dane logowania są niepoprawne, spróbuj jeszcze raz",
            additionalFormError:
              "jeśli nie pamiętasz hasła, skorzystaj z opcji odzyskiwania hasła",
          };
        }
        return context;
      }),
      validateForm: assign<IContext, IEvent>((context) => {
        let emailError: string | undefined;
        let passwordError: string | undefined;
        if (!context.email.trim() || !isValidEmail(context.email)) {
          emailError = "wpisz poprawny adres e-mail, np. jankowalski@domena.pl";
        }

        if (!context.password.trim()) {
          passwordError = "wpisz hasło do konta";
        }

        return {
          formError: undefined,
          additionalFormError: undefined,
          emailError,
          passwordError,
        };
      }),
      setInputValue: assign<IContext, IEvent>((context, event) => {
        if (event.type === "SET_INPUT_VALUE") {
          return {
            [event.name]: event.value,
          };
        }
        return context;
      }),
    },
    guards: {
      hasAuthCode: ({ authCode }) => {
        return !!authCode;
      },
      hasNoInputErrors: ({ emailError, passwordError }) => {
        return emailError === undefined && passwordError === undefined;
      },
    },
    services: {
      loginWithForm: ({ email, password, flow, offerIds, mode }) => {
        return async (callback) => {
          try {
            const result = await login({ email, password });
            if (result.isError && result.err.code === "INVALID_CREDENTIALS") {
              return callback({ type: "GENERAL_ERROR" });
            } else {
              pushloginEvent({ method: "credentials" });
              executeAfterLoginProcess(callback, flow, mode, offerIds);
            }
          } catch {
            callback({ type: "GENERAL_ERROR" });
          }
        };
      },
      loginWithCode: ({ authCode, flow, offerIds, mode }) => {
        return async (callback) => {
          try {
            (
              await loginWithIdentityProvider({
                code: authCode || "",
                redirectUri:
                  window.location.origin + portalRoutes.zaloguj.index.get(),
              })
            ).unwrap();

            pushloginEvent({ method: "social media" });
            executeAfterLoginProcess(callback, flow, mode, offerIds);
          } catch {
            callback({ type: "GENERAL_ERROR" });
          }
        };
      },
    },
  },
);

const modeToOrchestratorMap = {
  ftth: "NJU_FTTH_ONBOARDING",
  mobile: "NJU_MOBILE_ONBOARDING",
  postpaid: "NJU_MULTIORDER_ONBOARDING",
} as const;

async function executeAfterLoginProcess(
  send: Sender<IEvent>,
  flow: "ONBOARDING" | "multiOrder" | undefined,
  mode: IOfferMode,
  offerIds?: string[],
) {
  try {
    // Fetch existing orchestrators for logged user
    const orchestrators = (await fetchOrchestrators()).unwrap();

    // Standard login process. If there is no onboarding orchestrator we create one.
    // Otherwise, we just send LOGIN_SUCCESSFUL event.
    if (flow === undefined) {
      const orchestrator = getOrchestratorForMode(orchestrators, mode);

      if (!orchestrator) {
        const createdOrchestrator = await createOrchestrator(
          modeToOrchestratorMap[mode],
          {},
        );
        if (createdOrchestrator.isError) {
          if (createdOrchestrator.err.code === "CLIENT_HAS_UNPAID_INVOICES") {
            const user = (await info()).unwrap();

            return send({
              type: "LOGIN_SUCCESSFUL",
              user,
            });
          }
        } else {
          return send({
            type: "LOGIN_SUCCESSFUL",
            orchestrator: createdOrchestrator.value,
          });
        }
      } else {
        return send({
          type: "LOGIN_SUCCESSFUL",
          orchestrator,
        });
      }
    }

    // ONBOARDING flow. If there is no onboarding orchestrator we create one.
    // Then we create order for provided installationToken and offerIds.
    // After that we send LOGIN_SUCCESSFUL event.
    // For existing orchestrator we check if current state is START.
    // If it is we create new order for provided installationToken and offerIds.
    // Otherwise we just send INVALID_ORCHESTRATOR_STATE event.

    if (flow === "ONBOARDING" || flow === "multiOrder") {
      if (mode === "mobile") {
        if (!offerIds) {
          return send({ type: "GENERAL_ERROR" });
        }
        //We check if orchestrator is FTTH and is in state !== START and COMPLETED
        if (
          orchestrators.ftth &&
          onboardingInProgress(
            orchestrators.ftth.state,
            orchestrators.ftth.type,
          )
        ) {
          //if yes that means that we have ftth order in progress and we can start mobile order
          return send({
            type: "ANOTHER_ORDER_IN_PROGRESS",
          });
        }

        if (flow === "ONBOARDING") {
          //If we have proper orchestrator for this mode we need to check if it is in START state
          //if yes then we are logged in successfully
          if (orchestrators.mobile && orchestrators.mobile.state === "START") {
            return send({
              type: "LOGIN_SUCCESSFUL",
              orchestrator: orchestrators.mobile,
            });
          }

          //If we are in state other than START then we are in wrong orchestrator state
          //and we are redirected to ups screen
          if (orchestrators.mobile && orchestrators.mobile.state !== "START") {
            return send({
              type: "INVALID_ORCHESTRATOR_STATE",
              orchestrator: orchestrators.mobile,
            });
          }

          //In all other cases we should be logged in
          const user = (await info()).unwrap();
          return send({
            type: "LOGIN_SUCCESSFUL",
            orchestrator: orchestrators.mobile,
            user,
          });
        }

        if (flow === "multiOrder") {
          if (
            orchestrators.multiOrder &&
            orchestrators.multiOrder.state === "START"
          ) {
            return send({
              type: "LOGIN_SUCCESSFUL",
              orchestrator: orchestrators.multiOrder,
            });
          }

          //In all other cases we should be logged in
          const user = (await info()).unwrap();
          return send({
            type: "LOGIN_SUCCESSFUL",
            orchestrator: orchestrators.multiOrder,
            user,
          });
        }
      }

      if (mode === "ftth") {
        //We check if we have mobile orchestrator and if state !== COMPLETED
        //If yes then it means that another order is in progress
        if (
          orchestrators.mobile &&
          onboardingInProgress(
            orchestrators.mobile.state,
            orchestrators.mobile.type,
          )
        ) {
          return send({
            type: "ANOTHER_ORDER_IN_PROGRESS",
          });
        }
        //Rest of the logic didn't change
        const installationToken =
          window.sessionStorage.getItem("installationToken");

        if (!installationToken || !offerIds) {
          return send({ type: "GENERAL_ERROR" });
        }

        if (!orchestrators.ftth) {
          const orchestrator = await createOrchestrator(
            "NJU_FTTH_ONBOARDING",
            {},
          );

          if (orchestrator.isError) {
            if (orchestrator.err.code === "CLIENT_HAS_UNPAID_INVOICES") {
              const user = (await info()).unwrap();

              return send({
                type: "LOGIN_SUCCESSFUL",
                user,
              });
            }

            return send({ type: "GENERAL_ERROR" });
          }

          const orchestratorWithOrder = (
            await createFtthOrder({
              mode: "ftth",
              installationToken,
              offerIds,
              orchestratorId: orchestrator.value.id,
            })
          ).unwrap();

          return send({
            type: "LOGIN_SUCCESSFUL",
            orchestrator: orchestratorWithOrder,
          });
        } else {
          if (orchestrators.ftth.state === "START") {
            const orchestratorWithOrder = (
              await createFtthOrder({
                mode: "ftth",
                installationToken,
                offerIds,
                orchestratorId: orchestrators.ftth.id,
              })
            ).unwrap();
            return send({
              type: "LOGIN_SUCCESSFUL",
              orchestrator: orchestratorWithOrder,
            });
          } else {
            return send({
              type: "INVALID_ORCHESTRATOR_STATE",
              orchestrator: orchestrators.ftth,
            });
          }
        }
      }
    }
  } catch {
    send({ type: "GENERAL_ERROR" });
  }
}
